/**
 * LPS Marquee — drives <lps-marquee> elements (see sections/LPS-marquee-scroll.liquid).
 * No dependency on the theme's marquee-component/@theme/component — plain JS, matching
 * the other LPS-prefixed custom sections in this theme.
 *
 * Two movement modes (data-movement-mode):
 * - "auto": continuous CSS animation, same technique as the native Marquee section
 *   (a duplicated content track animates via @keyframes; see the section's stylesheet).
 * - "scroll": no animation — content shifts left as the visitor scrolls down and right
 *   as they scroll up, by directly setting the wrapper's transform from the scroll delta.
 *
 * Both modes need enough repeated copies of the content to fill the marquee's width plus
 * one extra full copy appended for a seamless loop — that part is shared.
 */
(function() {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initMarquee(el) {
    if (el.hasAttribute('data-lps-marquee-inited')) return;
    el.setAttribute('data-lps-marquee-inited', '1');

    var wrapper = el.querySelector('.lps-marquee__wrapper');
    var content = el.querySelector('.lps-marquee__content');
    var items = el.querySelector('.lps-marquee__items');
    if (!wrapper || !content || !items || !items.children.length) return;

    var mode = el.getAttribute('data-movement-mode') === 'scroll' ? 'scroll' : 'auto';
    var direction = el.getAttribute('data-movement-direction') || 'normal';
    var speedFactor = parseFloat(el.getAttribute('data-speed-factor')) || 25;
    var pauseOnHover = el.getAttribute('data-pause-on-hover') === 'true';
    var sensitivity = parseFloat(el.getAttribute('data-scroll-sensitivity')) || 1;
    var invert = el.getAttribute('data-invert-scroll') === 'true';

    // Repeat the item set enough times to at least fill the marquee's own
    // width (so there's no gap before the loop repeats), then append one
    // more full copy of that repeated content as a sibling for the
    // seamless "scroll to -50%, snap back to 0" loop technique.
    function fillWidth() {
      var targetWidth = el.getBoundingClientRect().width;
      var itemsWidth = items.getBoundingClientRect().width;
      if (!itemsWidth) return;
      var needed = Math.max(1, Math.ceil(targetWidth / itemsWidth));
      var current = content.children.length;
      var i;
      if (needed > current) {
        for (i = 0; i < needed - current; i++) {
          content.appendChild(items.cloneNode(true));
        }
      } else if (needed < current) {
        for (i = 0; i < current - needed; i++) {
          if (content.lastElementChild && content.lastElementChild !== items) {
            content.removeChild(content.lastElementChild);
          }
        }
      }
    }

    function duplicateForLoop() {
      var existingClone = wrapper.children.length > 1 ? wrapper.lastElementChild : null;
      if (existingClone) existingClone.remove();
      var clone = content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      wrapper.appendChild(clone);
    }

    function rebuild() {
      fillWidth();
      duplicateForLoop();
    }

    rebuild();

    if (mode === 'scroll') {
      initScrollMode();
    } else {
      initAutoMode();
    }

    function initAutoMode() {
      el.classList.add('lps-marquee--auto');
      el.style.setProperty('--lps-marquee-direction', direction);

      function applySpeed() {
        var numberOfCopies = content.children.length;
        var speed = Math.sqrt(numberOfCopies) * speedFactor;
        el.style.setProperty('--lps-marquee-speed', speed + 's');
      }
      applySpeed();

      if (pauseOnHover) {
        el.addEventListener('pointerenter', function() {
          el.classList.add('lps-marquee--slow');
        });
        el.addEventListener('pointerleave', function() {
          el.classList.remove('lps-marquee--slow');
        });
      }

      var resizeTimer;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
          rebuild();
          applySpeed();
        }, 200);
      });
    }

    function initScrollMode() {
      el.classList.add('lps-marquee--scroll');

      var offset = 0;
      var lastScrollY = window.scrollY;
      var loopWidth = 0;
      var ticking = false;

      function measure() {
        // The distance from the start of .content to the start of its
        // cloned sibling (wrapper's 2nd child) — NOT just content's own
        // width, since that would ignore the flex gap between them and
        // the loop would drift out of alignment by one gap's width every
        // wrap. Measuring the actual gap between them directly sidesteps
        // needing to know the gap value here at all.
        var clone = wrapper.children[1];
        loopWidth = clone
          ? clone.getBoundingClientRect().left - content.getBoundingClientRect().left
          : content.getBoundingClientRect().width;
      }
      measure();

      function render() {
        wrapper.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function() {
          var currentScrollY = window.scrollY;
          var delta = currentScrollY - lastScrollY;
          lastScrollY = currentScrollY;

          var move = delta * sensitivity * (invert ? -1 : 1);
          // Scrolling down moves the content left by default — feels like
          // the marquee is "left behind" as the page moves past it.
          offset -= move;

          if (loopWidth > 0) {
            // Wrap into (-loopWidth, 0] so it loops seamlessly forever in
            // either direction, instead of drifting off and running out of
            // duplicated content.
            offset = offset % loopWidth;
            if (offset > 0) offset -= loopWidth;
          }

          render();
          ticking = false;
        });
      }

      window.addEventListener('scroll', onScroll, { passive: true });

      var resizeTimer;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
          rebuild();
          measure();
        }, 200);
      });
    }
  }

  ready(function() {
    var els = document.querySelectorAll('[data-lps-marquee]');
    for (var i = 0; i < els.length; i++) initMarquee(els[i]);
  });
})();
